import type {
  CarRecentSelectionProps,
  FlightRecentSelectionProps,
  HotelRecentSelectionProps,
  PriceInfo,
  RecentSelectionProps,
} from '@pcln/horizon-customer-growth-components'
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays'
import { parse } from 'date-fns/parse'
import type {
  AbandonedCarSelection,
  AbandonedFlightSelection,
  AbandonedHotelSelection,
  AbandonedSelection,
} from './types'
import { DAY_DATE_YEAR_FORMAT } from './utils'

export function getNightCount(checkInDate: string, checkOutDate: string): number {
  const checkIn = parse(checkInDate, DAY_DATE_YEAR_FORMAT, new Date())
  const checkOut = parse(checkOutDate, DAY_DATE_YEAR_FORMAT, new Date())
  const nights = differenceInCalendarDays(checkOut, checkIn)
  return nights > 0 ? nights : 1
}

function buildHotelPriceInfo(selection: AbandonedHotelSelection): PriceInfo | undefined {
  const { checkInDate, checkOutDate, hotelTotalPriceExcludingTaxes, numRooms } = selection

  if (!hotelTotalPriceExcludingTaxes?.amount) return undefined

  const nights = getNightCount(checkInDate, checkOutDate)

  const nightLabel = `${nights} night${nights === 1 ? '' : 's'}`
  const roomLabel = `${numRooms} room${numRooms === 1 ? '' : 's'}`

  return {
    currencySymbol: hotelTotalPriceExcludingTaxes.currencyPrefix,
    totalPrice: Math.floor(Number(hotelTotalPriceExcludingTaxes.amount)).toString(),
    priceContext: `${nightLabel}, ${roomLabel}`,
  }
}

const toHotelRecentSelectionProps = (selection: AbandonedHotelSelection): HotelRecentSelectionProps => {
  const {
    checkInDate,
    checkOutDate,
    headline,
    imageSrc,
    isFavorite,
    location,
    numReviews,
    numStars,
    overallRating,
    subheadline,
  } = selection

  return {
    ariaLabel: headline,
    checkIn: { date: checkInDate },
    checkOut: { date: checkOutDate },
    headline,
    imageSrc: imageSrc ?? '',
    isFavorite,
    locationText: location || subheadline,
    price: buildHotelPriceInfo(selection),
    productType: 'hotels',
    reviews:
      overallRating && numReviews
        ? { rating: String(overallRating), reviewCount: numReviews.toLocaleString() }
        : { rating: '', reviewCount: '' },
    starRating: String(numStars ?? ''),
  } satisfies HotelRecentSelectionProps
}

const toCarRecentSelectionProps = ({
  brandImg,
  dropOffDateInfo,
  dropOffDate,
  headline,
  imageSrc,
  numAdults,
  numAdultsCount,
  pickUpDateInfo,
  pickUpDate,
  pickupLocation,
}: AbandonedCarSelection): CarRecentSelectionProps => {
  const passengerCount = numAdultsCount ?? (Number(numAdults) || 1)
  const parseCarDateTime = (value?: string) => {
    if (!value) {
      return { date: '' }
    }

    const [date, time] = value.split(' at ')
    return time ? { date, time } : { date: value }
  }

  return {
    ariaLabel: headline,
    brandImg,
    dropOff: dropOffDateInfo ?? parseCarDateTime(dropOffDate),
    headline,
    imageSrc,
    passengerText: `${passengerCount} Passenger${passengerCount === 1 ? '' : 's'}`,
    pickUp: pickUpDateInfo ?? parseCarDateTime(pickUpDate),
    pickupLocation,
    productType: 'cars',
  } satisfies CarRecentSelectionProps
}

const toFlightRecentSelectionProps = ({
  cabinClass,
  departDate,
  departDestAirportCode,
  departOriginAirportCode,
  headline,
  numAdults,
  returnDate,
  returnDestAirportCode,
  returnOriginAirportCode,
  tripType,
}: AbandonedFlightSelection): FlightRecentSelectionProps => {
  const CABIN_CLASS_LABEL: Record<string, string> = {
    ECO: 'Economy',
    PEC: 'Premium Economy',
    BUS: 'Business',
    FST: 'First',
  }
  const TRIP_TYPE_LABELS: Partial<Record<AbandonedFlightSelection['tripType'], string>> = {
    MULTI_DESTINATION: 'Multi-city',
    ONE_WAY: 'One-way',
    ROUND_TRIP: 'Round-trip',
  }

  const resolvedCabinClass = (cabinClass && CABIN_CLASS_LABEL[cabinClass]) || cabinClass || 'Economy'
  const flightTypeText = TRIP_TYPE_LABELS[tripType] ?? tripType
  const passengerCount = numAdults > 0 ? numAdults : 1

  return {
    ariaLabel: headline,
    departFlight: {
      airportCodes: `${departOriginAirportCode} - ${departDestAirportCode}`,
      dateInfo: { date: departDate },
    },
    headline,
    occupancyText: `${passengerCount} Adult${passengerCount > 1 ? 's' : ''}, ${resolvedCabinClass}`,
    productType: 'flights',
    returnFlight: returnDate
      ? {
          airportCodes: `${returnOriginAirportCode} - ${returnDestAirportCode}`,
          dateInfo: { date: returnDate },
        }
      : undefined,
    subtitle: `${flightTypeText} flight`,
  } satisfies FlightRecentSelectionProps
}

const recentSelectionMapper = (selection: AbandonedSelection): RecentSelectionProps => {
  const onClick = () => {
    // TODO: fire analytics event
    window.open(selection.linkUrl)
  }

  switch (selection.productType) {
    case 'hotels':
      return { ...toHotelRecentSelectionProps(selection), onClick }
    case 'cars':
      return { ...toCarRecentSelectionProps(selection), onClick }
    case 'flights':
      return { ...toFlightRecentSelectionProps(selection), onClick }
  }
}

export { recentSelectionMapper }
